import React, { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { useAuth } from '../context/AppContext'
import { LayoutTemplate, Pencil, Trash2, Search } from 'lucide-react'

function FlowTemplateModal({ template, onClose, onSaved }) {
  const isEdit = !!template

  const [form, setForm] = useState({
    name: template?.name || '',
    vertical: template?.vertical || '',
    description: template?.description || '',
  })
  const [verticals, setVerticals] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get('/config/verticals')
      .then(res => setVerticals(Array.isArray(res.data) ? res.data : []))
      .catch(() => setVerticals([]))
  }, [])

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        vertical: form.vertical,
        description: form.description.trim() || undefined,
      }
      if (isEdit) {
        await api.patch(`/flow-templates/${template.id}`, payload)
        toast.success('Template updated')
      } else {
        // Creation from this admin screen has no source campaign graph to
        // copy - it only exists so staff can fix a template's metadata
        // (rename, re-vertical, re-describe). Building a template from an
        // empty graph isn't a supported flow here; use "Save as Template"
        // from a campaign's flow editor to create one.
        toast.error('New templates are created from a campaign\'s flow editor via "Save as Template".')
        setSaving(false)
        return
      }
      onSaved()
      onClose()
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || 'Error saving template'
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Template</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="input" value={form.name} onChange={e => f('name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Vertical</label>
              <select className="select" value={form.vertical} onChange={e => f('vertical', e.target.value)} required>
                <option value="">— Select Vertical —</option>
                {verticals.map(v => (
                  <option key={v.identifier} value={v.identifier}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="input" rows={3} value={form.description} onChange={e => f('description', e.target.value)} />
            </div>
            {error ? <div className="partner-modal-error">{error}</div> : null}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <div className="spinner" /> : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function FlowTemplatesPage() {
  const { isAdmin } = useAuth()
  const canManage = isAdmin

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [editorFor, setEditorFor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get('/flow-templates/', { params: { limit: 100, page_size: 100, per_page: 100 } })
      setTemplates(res.data?.items || res.data || [])
    } catch (err) {
      setError(
        err.response?.status === 403
          ? "You don't have permission to view flow templates."
          : err.response?.data?.detail || 'Error loading flow templates.'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this flow template? This cannot be undone.')) return
    try {
      await api.delete(`/flow-templates/${templateId}`)
      toast.success('Template deleted')
      load()
    } catch (err) {
      const detail = err.response?.data?.detail || (err.response?.status === 403
        ? "You don't have permission to delete flow templates."
        : 'Error deleting template')
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail))
    }
  }

  const filtered = templates.filter(t =>
    t.name?.toLowerCase().includes(q.toLowerCase()) || t.vertical?.toLowerCase().includes(q.toLowerCase())
  )

  return (
    <div className="fade-in page-shell partners-page-shell">
      <section className="page-hero partners-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Routing Engine</span>
          <h1>Flow Templates.</h1>
          <p>
            Reusable campaign routing graphs staff can apply to a campaign's flow instead of building it from scratch.
            New templates are created from a campaign's flow editor via "Save as Template".
          </p>
        </div>
      </section>

      <section className="card partners-filters partners-filters-panel" style={{ display: 'flex', gap: 12 }}>
        <div className="search-bar search-bar-elevated" style={{ flex: 1 }}>
          <span className="search-icon"><Search size={14} /></span>
          <input
            className="input"
            placeholder="Search by name or vertical…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button className="btn btn-ghost" onClick={load}>Refresh</button>
      </section>

      {loading ? <div className="partner-empty-block">Loading templates…</div> : null}
      {error ? <div className="partner-modal-error partner-page-error">{error}</div> : null}

      {!loading && !error && filtered.length === 0 && (
        <div className="card partner-empty-card">
          No flow templates found.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="partners-grid">
          {filtered.map(t => (
            <div key={t.id} className="card fade-in partners-card">
              <div className="partners-card-head">
                <div className="partners-card-title-wrap">
                  <div className="partners-card-title">
                    <LayoutTemplate size={14} style={{ marginRight: 6, color: 'var(--accent)' }} />
                    {t.name}
                  </div>
                  <div className="partners-card-subtitle">
                    Vertical: <strong>{t.vertical}</strong> | Template ID: {t.id}
                  </div>
                </div>
              </div>

              <div style={{ padding: '12px 16px', background: 'var(--bg-raised)', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t.description || 'No description.'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                  Updated {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}
                </div>
              </div>

              {canManage && (
                <div className="partners-card-actions" style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost partners-card-btn" style={{ flex: 1 }} onClick={() => setEditorFor(t)}>
                    <Pencil size={13} style={{ marginRight: 4 }} /> Edit
                  </button>
                  <button className="btn btn-ghost partners-card-btn" style={{ color: 'var(--red)' }} onClick={() => handleDelete(t.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editorFor && (
        <FlowTemplateModal
          template={editorFor}
          onClose={() => setEditorFor(null)}
          onSaved={load}
        />
      )}
    </div>
  )
}
